import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTelegram } from '../../context/TelegramContext';
import SettingsSection from './SettingsSection';

const InputGroup = styled.div`
  display: flex;
  margin-bottom: 12px;
  width: 100%;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 10px 0 0 10px;
  background: #f0f3f9;
  color: #333;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
              inset -2px -2px 5px rgba(255, 255, 255, 0.5);
  
  &:focus {
    outline: none;
  }
`;

const ActionButton = styled.button`
  background: #4a6fa1;
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 0 10px 10px 0;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: #3b5c8a;
  }
`;

const AccountsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const AccountCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const AccountName = styled.span`
  font-weight: 500;
  color: #333;
`;

const DeleteButton = styled.button`
  background: rgba(229, 62, 62, 0.1);
  border: none;
  color: #e53e3e;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(229, 62, 62, 0.2);
  }
`;

const NoAccountsMessage = styled.p`
  color: #8c96a5;
  text-align: center;
  font-style: italic;
  margin: 10px 0;
`;

const FormMessage = styled.p`
  margin: 8px 0;
  font-size: 14px;
  color: ${props => props.isError ? '#e53e3e' : '#38a169'};
`;

const LinkedAccountsSection = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const [targetUserId, setTargetUserId] = useState('');
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', isError: false });

  useEffect(() => {
    fetchLinkedAccounts();
  }, [telegramUserId]);

  const fetchLinkedAccounts = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://fftg.farodinprojects.org:3000/get_linked_accounts?user_id=${telegramUserId}`);
      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setLinkedAccounts(data);
      } else {
        setMessage({ text: t('linked_account_error'), isError: true });
      }
    } catch (err) {
      setMessage({ text: t('linked_account_error'), isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    
    if (!targetUserId || targetUserId === telegramUserId) {
      setMessage({ text: t('self_link_error'), isError: true });
      return;
    }
    
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/request_merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initiator_id: telegramUserId,
          target_id: targetUserId
        })
      });

      if (response.ok) {
        setMessage({ text: t('add_account_success'), isError: false });
        setTargetUserId('');
        await fetchLinkedAccounts();
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.error || t('add_account_error'), isError: true });
      }
    } catch (error) {
      setMessage({ text: t('add_account_error'), isError: true });
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/delete_linked_account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: telegramUserId, account_id: accountId })
      });

      if (response.ok) {
        setMessage({ text: t('delete_account_success'), isError: false });
        // Обновляем список аккаунтов без перезагрузки
        setLinkedAccounts(prevAccounts => 
          prevAccounts.filter(account => account.id !== accountId)
        );
      } else {
        setMessage({ text: t('delete_account_error'), isError: true });
      }
    } catch (error) {
      setMessage({ text: t('delete_account_error'), isError: true });
    }
  };

  return (
    <SettingsSection 
      title={t('add_account_title')} 
      description={t('add_account_description')}
    >
      <form onSubmit={handleAddAccount}>
        <InputGroup>
          <Input 
            type="text" 
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            placeholder={t('enter_user_id')} 
            required
          />
          <ActionButton type="submit">{t('add_button')}</ActionButton>
        </InputGroup>
      </form>
      
      {message.text && (
        <FormMessage isError={message.isError}>{message.text}</FormMessage>
      )}
      
      <SettingsSection title={t('linked_accounts_title')}>
        {isLoading ? (
          <NoAccountsMessage>{t('loading')}</NoAccountsMessage>
        ) : linkedAccounts.length > 0 ? (
          <AccountsList>
            {linkedAccounts.map(account => (
              <AccountCard key={account.id}>
                <AccountInfo>
                  <img 
                    src={account.photo_url || 'https://via.placeholder.com/32'} 
                    alt={`${account.first_name || 'User'}`}
                  />
                  <AccountName>{account.first_name || 'User'}</AccountName>
                </AccountInfo>
                <DeleteButton onClick={() => handleDeleteAccount(account.id)}>
                  {t('delete_button')}
                </DeleteButton>
              </AccountCard>
            ))}
          </AccountsList>
        ) : (
          <NoAccountsMessage>{t('no_accounts_message')}</NoAccountsMessage>
        )}
      </SettingsSection>
    </SettingsSection>
  );
};

export default LinkedAccountsSection;